import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';

import { useForm, FormProvider } from 'react-hook-form';

import { ContentToggler } from './Container';

import {
  ButtonStyled,
  DialogContentStyled,
  DialogActionsStyled,
} from 'elements/mismosh';
import { Error } from './styles';

import { resetPassword } from 'components/authorization/cognitoUtils';
import { Password } from './password';

type FormData = {
  oldPassword: string;
  password: string;
  confirmedPassword: string;
};

interface Props {
  switchToClose: ContentToggler;
}

/**
 * For a logged in user to change their password.
 *
 * This will only work for those with verified emails, as the code is sent
 * by email.
 */
export const ResetPassword: React.FC<Props> = ({ switchToClose }) => {
  const [apiError, setApiError] = useState('');
  const methods = useForm<FormData>({
    defaultValues: { password: '', oldPassword: '', confirmedPassword: '' },
  });

  const handleClose = () => {
    switchToClose();
    setApiError(undefined);
  };

  const onSubmit = async (
    { oldPassword, password, confirmedPassword }: FormData,
    e: React.SyntheticEvent,
  ) => {
    e.preventDefault();
    try {
      if (password !== confirmedPassword)
        throw { message: 'New passwords do not match.' };

      await resetPassword(oldPassword, password);
      handleClose();
    } catch (err) {
      setApiError(err?.message);
      console.log('error...: ', err);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle id="resetPassword-modal">Reset Password</DialogTitle>
          <DialogContentStyled>
            <Password name="oldPassword" label="Old Password" />
            {methods.formState.errors.oldPassword && (
              <Error>Field is required</Error>
            )}

            <Password name="password" label="New Password" />
            {methods.formState.errors.password && (
              <Error>Field is required</Error>
            )}

            <Password name="confirmedPassword" label="Confirm New Password" />
            {methods.formState.errors.confirmedPassword && (
              <Error>Field is required</Error>
            )}

            {!!apiError && <Error>{apiError}</Error>}
          </DialogContentStyled>
          <DialogActionsStyled>
            <ButtonStyled
              name="resetPassword-submit"
              variant="contained"
              color="primary"
              type="submit"
            >
              Continue
            </ButtonStyled>
          </DialogActionsStyled>
        </form>
      </FormProvider>
    </Dialog>
  );
};
